<template>
  <div class="fixed inset-0 z-50 flex items-center justify-center bg-gray-900 bg-opacity-70 backdrop-blur-sm animate-modalFade">
    <!-- Container Modal -->
    <div class="w-full m-3 max-w-3xl bg-gray-800 text-gray-300 rounded-lg shadow-lg overflow-hidden border border-gray-700 animate-scaleIn">
      <!-- Header -->
      <div class="flex items-center justify-between p-4 border-b border-gray-700 bg-gray-800">
        <div class="flex items-center gap-3">
          <img
            :src="localItem.img || itemPlaceholder"
            alt="Item Image"
            class="w-12 h-12 sm:w-20 sm:h-20 object-cover rounded-md border-2"
            :class="localItem.rarityColors?.borderColor || 'border-gray-600'"
            @error="handleImageError"
          />
          <div class="space-y-0.5">
            <h1 class="text-lg font-semibold" :class="localItem.rarityColors?.textColor || 'text-indigo-300'">
              {{ localItem.name || 'ไม่ระบุ' }} <span v-if="localItem.level">+{{ localItem.level }}</span>
            </h1>
            <p class="text-indigo-200 text-xs sm:text-sm">ประเภท: {{ localItem.type || 'ไม่ระบุ' }} {{ localItem.subtype ? `(${localItem.subtype})` : '' }}</p>
            <div v-if="localItem.rarity" class="flex items-center gap-1">
              <span class="text-yellow-400 text-xs sm:text-sm">{{ localItem.rarity }}</span>
              <i class="fas fa-star text-yellow-400 text-xs sm:text-sm"></i>
            </div>
          </div>
        </div>
        <button @click="closeModal" class="text-gray-300 hover:text-indigo-300 transition-colors duration-200" aria-label="ปิด">
          <i class="fas fa-times text-base"></i>
        </button>
      </div>

      <!-- Tabs -->
      <div class="px-4 pt-4">
        <div class="flex border-b border-gray-700">
          <button
            @click="activeTab = 'info'"
            class="px-3 py-2 text-xs sm:text-sm font-medium transition-colors duration-200"
            :class="activeTab === 'info' ? 'text-indigo-300 border-b-2 border-indigo-500' : 'text-indigo-200 hover:bg-gray-700 hover:text-indigo-300'"
            :aria-selected="activeTab === 'info'"
          >ข้อมูลไอเทม</button>
          <button
            @click="activeTab = 'dropLocations'"
            class="px-3 py-2 text-xs sm:text-sm font-medium transition-colors duration-200"
            :class="activeTab === 'dropLocations' ? 'text-indigo-300 border-b-2 border-indigo-500' : 'text-indigo-200 hover:bg-gray-700 hover:text-indigo-300'"
            :aria-selected="activeTab === 'dropLocations'"
          >สถานที่ดรอป</button>
          <button
            v-if="localItem.decomposable"
            @click="activeTab = 'decompose'"
            class="px-3 py-2 text-xs sm:text-sm font-medium transition-colors duration-200"
            :class="activeTab === 'decompose' ? 'text-indigo-300 border-b-2 border-indigo-500' : 'text-indigo-200 hover:bg-gray-700 hover:text-indigo-300'"
            :aria-selected="activeTab === 'decompose'"
          >แยกชิ้นส่วน</button>
          <button
            v-if="localItem.upgradable"
            @click="activeTab = 'upgrade'"
            class="px-3 py-2 text-xs sm:text-sm font-medium transition-colors duration-200"
            :class="activeTab === 'upgrade' ? 'text-indigo-300 border-b-2 border-indigo-500' : 'text-indigo-200 hover:bg-gray-700 hover:text-indigo-300'"
            :aria-selected="activeTab === 'upgrade'"
          >อัพเกรด</button>
        </div>
      </div>

      <!-- Content -->
      <div class="px-4 py-4 space-y-4 overflow-y-auto max-h-96" id="style-1">
        <div v-if="itemLoading" class="flex items-center justify-center py-4">
          <div class="animate-spin rounded-full h-6 w-6 border-t-2 border-indigo-600"></div>
        </div>
        <div v-if="error" class="text-red-300 text-xs sm:text-sm text-center">{{ error }}</div>

        <!-- Tab: Item Info -->
        <div v-if="activeTab === 'info' && !itemLoading">

          <!-- Basic Information -->
          <section class="bg-gray-700 p-4 rounded-md">
            <h2 class="text-sm font-semibold text-indigo-200 mb-2">ข้อมูลพื้นฐาน</h2>
            <div class="grid grid-cols-2 gap-3 text-xs sm:text-sm">
              <div v-if="localItem.type !== 'ทั่วไป'" class="flex items-center gap-2">
                <span class="text-indigo-200">เลเวลที่ใช้ได้: <strong class="text-gray-100">{{ localItem.requiredLevel || 0 }}</strong></span>
              </div>
              <div class="flex items-center gap-2">
                <span class="text-indigo-200">น้ำหนัก: <strong class="text-gray-100">{{ localItem.weight || 0 }}</strong> กก.</span>
              </div>
              <div class="flex items-center gap-2">
                <span class="text-indigo-200">ราคา:</span>
                <i v-if="localItem.currencyType === 'gold'" class="fa-solid fa-g text-yellow-400"></i>
                <i v-else-if="localItem.currencyType === 'diamond'" class="fa-solid fa-gem text-blue-400"></i>
                <i v-else class="fa-solid fa-coins text-gray-100"></i>
                <strong class="text-gray-100">{{ localItem.price?.toLocaleString() || 0 }}</strong>
              </div>
              <div class="flex items-center gap-2">
                <span class="text-indigo-200">โอนได้: <strong class="text-gray-100">{{ localItem.transfer ? 'ใช่' : 'ไม่' }}</strong></span>
              </div>
            </div>
          </section>

          <!-- Description -->
          <section v-if="localItem.description" class="mt-4 bg-gray-700 p-4 rounded-md">
            <h2 class="text-sm font-semibold text-indigo-200 mb-2">คำอธิบาย</h2>
            <p class="text-indigo-200 text-xs sm:text-sm leading-relaxed">{{ localItem.description }}</p>
          </section>

          <!-- ========== Card Section รวม ติดตั้งแล้ว + ยังไม่ติดตั้ง ในกล่องเดียว ========== -->
          <section v-if="localItem.type === 'อวัยวะจักรกล'" class="mt-4 bg-gray-700 p-4 rounded-md">
            <!-- Header + Slot Counter -->
            <div class="flex items-center justify-between mb-3">
              <h2 class="text-sm font-semibold text-indigo-200">การ์ด</h2>
              <div class="flex items-center gap-1.5 text-xs">
                <span class="text-gray-400">ช่องที่ใช้:</span>
                <span :class="cardData.length > 0 ? 'text-green-400 font-bold' : 'text-gray-400'">
                  {{ cardData.length }}
                </span>
                <span class="text-gray-500">/</span>
                <span class="text-gray-300 font-bold">{{ cardData.length + availableCards.length }}</span>
                <span class="text-gray-400">ช่อง</span>
              </div>
            </div>

            <!-- Slot bar -->
            <div class="flex gap-1 mb-4">
              <div
                v-for="n in (cardData.length + availableCards.length)"
                :key="n"
                class="h-1.5 flex-1 rounded-full"
                :class="n <= cardData.length ? 'bg-green-400' : 'bg-gray-500'"
              ></div>
            </div>

            <div v-if="availableCardsLoading" class="flex items-center justify-center py-4">
              <div class="animate-spin rounded-full h-6 w-6 border-t-2 border-indigo-600"></div>
            </div>

            <div v-else>
              <!-- ส่วนที่ 1: การ์ดที่ติดตั้งแล้ว (เข้ม) -->
              <template v-if="cardData.length > 0">
                <p class="text-xs text-green-400 font-semibold mb-2 uppercase tracking-wide">✓ ติดตั้งแล้ว</p>
                <div
                  v-for="card in cardData"
                  :key="'installed-' + card.id"
                  class="flex items-center gap-3 mb-3 border-b border-gray-600 pb-3 last:border-b-0 last:pb-0 last:mb-0"
                >
                  <img
                    :src="card.img || itemPlaceholder"
                    alt="Card Image"
                    class="w-10 h-10 object-cover rounded-md border-2"
                    :class="card.rarityColors?.borderColor || 'border-gray-600'"
                    @error="handleImageError"
                  />
                  <div class="flex-1">
                    <div class="flex items-center gap-2">
                      <span v-if="card.rarity" class="flex items-center gap-0.5 text-xs font-bold text-yellow-400">
                        <i class="fas fa-star" style="font-size: 9px;"></i>{{ card.rarity }}
                      </span>
                      <h3 class="text-xs sm:text-sm font-semibold text-indigo-200">{{ card.name || 'ไม่ระบุ' }}</h3>
                      <span class="text-xs text-green-400 bg-green-900 bg-opacity-40 px-1.5 py-0.5 rounded">ติดตั้งแล้ว</span>
                    </div>
                    <div v-if="card.stats && Object.values(card.stats).some(v => v > 0)" class="grid grid-cols-4 gap-2 text-xs sm:text-sm mt-2">
                      <div v-if="card.stats.stamina > 0" class="flex items-center gap-1"><i class="fas fa-heart text-indigo-400 mr-1"></i><span class="text-indigo-200">Stamina: <strong class="text-gray-100">{{ card.stats.stamina }}</strong></span></div>
                      <div v-if="card.stats.str > 0" class="flex items-center gap-1"><i class="fas fa-dumbbell text-indigo-400 mr-1"></i><span class="text-indigo-200">Str: <strong class="text-gray-100">{{ card.stats.str }}</strong></span></div>
                      <div v-if="card.stats.int > 0" class="flex items-center gap-1"><i class="fas fa-brain text-indigo-400 mr-1"></i><span class="text-indigo-200">Int: <strong class="text-gray-100">{{ card.stats.int }}</strong></span></div>
                      <div v-if="card.stats.agi > 0" class="flex items-center gap-1"><i class="fas fa-running text-indigo-400 mr-1"></i><span class="text-indigo-200">Agi: <strong class="text-gray-100">{{ card.stats.agi }}</strong></span></div>
                      <div v-if="card.stats.luck > 0" class="flex items-center gap-1"><i class="fas fa-dice text-indigo-400 mr-1"></i><span class="text-indigo-200">Luck: <strong class="text-gray-100">{{ card.stats.luck }}</strong></span></div>
                      <div v-if="card.stats.maxHp > 0" class="flex items-center gap-1"><i class="fas fa-heartbeat text-indigo-400 mr-1"></i><span class="text-indigo-200">Max HP: <strong class="text-gray-100">{{ card.stats.maxHp }}</strong></span></div>
                      <div v-if="card.stats.atk > 0" class="flex items-center gap-1"><i class="fas fa-fist-raised text-indigo-400 mr-1"></i><span class="text-indigo-200">Atk: <strong class="text-gray-100">{{ card.stats.atk }}</strong></span></div>
                      <div v-if="card.stats.evasion > 0" class="flex items-center gap-1"><i class="fas fa-running text-indigo-400 mr-1"></i><span class="text-indigo-200">Evasion: <strong class="text-gray-100">{{ card.stats.evasion }}</strong></span></div>
                      <div v-if="card.stats.critical > 0" class="flex items-center gap-1"><i class="fas fa-bolt text-indigo-400 mr-1"></i><span class="text-indigo-200">Critical: <strong class="text-gray-100">{{ card.stats.critical }}</strong></span></div>
                      <div v-if="card.stats.defense > 0" class="flex items-center gap-1"><i class="fas fa-shield-alt text-indigo-400 mr-1"></i><span class="text-indigo-200">Defense: <strong class="text-gray-100">{{ card.stats.defense }}</strong></span></div>
                    </div>
                  </div>
                </div>
              </template>

              <!-- เส้นคั่น -->
              <div v-if="cardData.length > 0 && availableCards.length > 0" class="border-t border-gray-500 my-3"></div>

              <!-- ส่วนที่ 2: การ์ดที่ยังไม่ติดตั้ง (จาง + stat preview toggle) -->
              <template v-if="availableCards.length > 0">
                <p class="text-xs text-gray-400 font-semibold mb-2 uppercase tracking-wide">○ ยังไม่ติดตั้ง <span class="text-gray-500 normal-case font-normal">(กดเพื่อดู stat preview)</span></p>
                <div
                  v-for="card in availableCards"
                  :key="'available-' + card.id"
                  class="mb-3 border-b border-gray-600 border-opacity-50 pb-3 last:border-b-0 last:pb-0 last:mb-0"
                >
                  <!-- Row หลัก กดเพื่อ toggle preview -->
                  <div
                    class="flex items-center gap-3 cursor-pointer select-none"
                    :class="previewCardId === card.id ? 'opacity-100' : 'opacity-50'"
                    @click="togglePreview(card.id)"
                  >
                    <img
                      :src="card.img || itemPlaceholder"
                      alt="Card Image"
                      class="w-10 h-10 object-cover rounded-md border-2 flex-shrink-0"
                      :class="card.rarityColors?.borderColor || 'border-gray-600'"
                      @error="handleImageError"
                    />
                    <div class="flex-1">
                      <div class="flex items-center gap-2">
                        <span v-if="card.rarity" class="flex items-center gap-0.5 text-xs font-bold text-yellow-400">
                          <i class="fas fa-star" style="font-size: 9px;"></i>{{ card.rarity }}
                        </span>
                        <h3 class="text-xs sm:text-sm font-semibold text-gray-300">{{ card.name || 'ไม่ระบุ' }}</h3>
                        <i
                          class="fas text-gray-400 text-xs ml-auto"
                          :class="previewCardId === card.id ? 'fa-chevron-up' : 'fa-chevron-down'"
                        ></i>
                      </div>
                    </div>
                  </div>

                  <!-- Stat Preview Panel (toggle) -->
                  <div v-if="previewCardId === card.id && card.stats" class="mt-2 ml-13 pl-13 bg-gray-800 rounded-md p-3 border border-indigo-500 border-opacity-40">
                    <p class="text-xs text-indigo-300 font-semibold mb-2">ถ้าติดตั้งการ์ดนี้ stat จะเปลี่ยนเป็น:</p>
                    <div class="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
                      <template v-for="(statKey, idx) in statPreviewKeys" :key="idx">
                        <div v-if="card.stats[statKey] > 0" class="flex items-center gap-1">
                          <i :class="statIcons[statKey]" class="text-indigo-400 w-3"></i>
                          <span class="text-gray-400 capitalize">{{ statKey }}:</span>
                          <span class="text-gray-300">{{ getCurrentStat(statKey) }}</span>
                          <i class="fas fa-arrow-right text-indigo-400 mx-0.5" style="font-size:8px"></i>
                          <span class="text-green-400 font-bold">{{ getCurrentStat(statKey) + card.stats[statKey] }}</span>
                          <span class="text-green-400">(+{{ card.stats[statKey] }})</span>
                        </div>
                      </template>
                    </div>
                  </div>
                </div>
              </template>

              <!-- ไม่มีการ์ดเลย -->
              <div v-if="cardData.length === 0 && availableCards.length === 0" class="text-indigo-200 text-xs sm:text-sm text-center py-2">
                ไม่มีการ์ดที่รองรับอวัยวะนี้
              </div>
            </div>
          </section>
          <!-- ========== End Card Section ========== -->

          <!-- Statistics -->
          <section v-if="hasStats" class="mt-4 bg-gray-700 p-4 rounded-md">
            <div v-if="hasMainStats">
              <h2 class="text-sm font-semibold text-indigo-200 mb-2">ค่าสถานะหลัก</h2>
              <div class="grid grid-cols-2 gap-3 text-xs sm:text-sm">
                <div v-if="showStamina" class="flex items-center gap-2">
                  <i class="fas fa-heart text-indigo-400 mr-1 text-lg"></i>
                  <span class="text-indigo-200">Stamina: <strong class="text-gray-100">{{ calculatedStats.stamina }}</strong></span>
                  <span v-if="isStatUpgradeable('stamina')" class="text-green-400 text-xs">(สามารถอัพเกรด)</span>
                </div>
                <div v-if="showStr" class="flex items-center gap-2">
                  <i class="fas fa-dumbbell text-indigo-400 mr-1 text-lg"></i>
                  <span class="text-indigo-200">Str: <strong class="text-gray-100">{{ calculatedStats.str }}</strong></span>
                  <span v-if="isStatUpgradeable('str')" class="text-green-400 text-xs">(สามารถอัพเกรด)</span>
                </div>
                <div v-if="showInt" class="flex items-center gap-2">
                  <i class="fas fa-brain text-indigo-400 mr-1 text-lg"></i>
                  <span class="text-indigo-200">Int: <strong class="text-gray-100">{{ calculatedStats.int }}</strong></span>
                  <span v-if="isStatUpgradeable('int')" class="text-green-400 text-xs">(สามารถอัพเกรด)</span>
                </div>
                <div v-if="showAgi" class="flex items-center gap-2">
                  <i class="fas fa-running text-indigo-400 mr-1 text-lg"></i>
                  <span class="text-indigo-200">Agi: <strong class="text-gray-100">{{ calculatedStats.agi }}</strong></span>
                  <span v-if="isStatUpgradeable('agi')" class="text-green-400 text-xs">(สามารถอัพเกรด)</span>
                </div>
                <div v-if="showLuck" class="flex items-center gap-2">
                  <i class="fas fa-dice text-indigo-400 mr-1 text-lg"></i>
                  <span class="text-indigo-200">Luck: <strong class="text-gray-100">{{ calculatedStats.luck }}</strong></span>
                  <span v-if="isStatUpgradeable('luck')" class="text-green-400 text-xs">(สามารถอัพเกรด)</span>
                </div>
              </div>
            </div>
            <div v-if="hasSubStats" class="mt-4">
              <h2 class="text-sm font-semibold text-indigo-200 mb-2">ค่าสถานะเสริม</h2>
              <div class="grid grid-cols-2 gap-3 text-xs sm:text-sm">
                <div v-if="calculatedSubStats.maxHp > 0" class="flex items-center gap-2"><span class="text-indigo-200">พลังชีวิต: <strong class="text-gray-100">{{ calculatedSubStats.maxHp }}</strong></span></div>
                <div v-if="calculatedSubStats.atk > 0" class="flex items-center gap-2"><span class="text-indigo-200">พลังโจมตี: <strong class="text-gray-100">{{ calculatedSubStats.atk }}</strong></span></div>
                <div v-if="calculatedSubStats.evasion > 0" class="flex items-center gap-2"><span class="text-indigo-200">หลบหลีก: <strong class="text-gray-100">{{ calculatedSubStats.evasion }}</strong></span></div>
                <div v-if="calculatedSubStats.critical > 0" class="flex items-center gap-2"><span class="text-indigo-200">คริติคอล: <strong class="text-gray-100">{{ calculatedSubStats.critical }}</strong></span></div>
                <div v-if="calculatedSubStats.defense > 0" class="flex items-center gap-2"><span class="text-indigo-200">พลังป้องกัน: <strong class="text-gray-100">{{ calculatedSubStats.defense }}</strong></span></div>
              </div>
            </div>
          </section>

          <!-- Upgrade Status -->
          <section v-if="localItem.upgradable" class="mt-4 bg-gray-700 p-4 rounded-md">
            <h2 class="text-sm font-semibold text-indigo-200 mb-2">สถานะการอัปเกรด</h2>
            <div class="grid grid-cols-2 gap-3 text-xs sm:text-sm">
              <div class="flex items-center gap-2"><span class="text-indigo-200">ระดับการอัปเกรดปัจจุบัน: <strong class="text-gray-100">{{ currentLevel }}</strong></span></div>
              <div class="flex items-center gap-2"><span class="text-indigo-200">จำนวนครั้งที่อัปเกรดได้เหลือ: <strong class="text-gray-100">{{ remainingUpgrades }}</strong></span></div>
            </div>
          </section>

          <!-- Food -->
          <section v-if="localItem.type === 'อาหาร'" class="mt-4 bg-gray-700 p-4 rounded-md">
            <h2 class="text-sm font-semibold text-indigo-200 mb-2">ข้อมูลอาหาร</h2>
            <div class="grid grid-cols-2 gap-3 text-xs sm:text-sm">
              <div class="flex items-center gap-2"><span class="text-indigo-200">ระดับอาหาร: <strong class="text-gray-100">{{ localItem.foodLevel || 0 }}</strong></span></div>
              <div class="flex items-center gap-2"><span class="text-indigo-200">ระดับความสุข: <strong class="text-gray-100">{{ localItem.happinessLevel || 0 }}</strong></span></div>
              <div class="flex items-center gap-2"><span class="text-indigo-200">ระดับพลังงาน: <strong class="text-gray-100">{{ localItem.energyLevel || 0 }}</strong></span></div>
            </div>
          </section>

          <!-- Heal -->
          <section v-if="localItem.type === 'รักษา'" class="mt-4 bg-gray-700 p-4 rounded-md">
            <h2 class="text-sm font-semibold text-indigo-200 mb-2">ข้อมูลการรักษา</h2>
            <div class="grid grid-cols-2 gap-3 text-xs sm:text-sm">
              <div class="flex items-center gap-2"><span class="text-indigo-200">HP ที่ได้รับ: <strong class="text-gray-100">{{ localItem.healHpPercentage || 0 }}%</strong></span></div>
              <div class="flex items-center gap-2"><span class="text-indigo-200">การรักษาติดเชื้อไวรัส: <strong class="text-gray-100">{{ localItem.virusHealPercentage || 0 }}%</strong></span></div>
            </div>
          </section>

          <!-- Trap -->
          <section v-if="localItem.type === 'กัปดัก'" class="mt-4 bg-gray-700 p-4 rounded-md">
            <h2 class="text-sm font-semibold text-indigo-200 mb-2">ข้อมูลกัปดัก</h2>
            <div class="grid grid-cols-2 gap-3 text-xs sm:text-sm">
              <div v-if="localItem.diceCount" class="flex items-center gap-2"><span class="text-indigo-200">จำนวนลูกเต๋า: <strong class="text-gray-100">{{ localItem.diceCount }}</strong></span></div>
              <div v-if="localItem.damage" class="flex items-center gap-2"><span class="text-indigo-200">สร้างความเสียหาย: <strong class="text-gray-100">{{ localItem.damage }}</strong></span></div>
              <div v-if="localItem.durationHours" class="flex items-center gap-2"><span class="text-indigo-200">ระยะเวลาชั่วโมง: <strong class="text-gray-100">{{ localItem.durationHours }}</strong></span></div>
              <div v-if="localItem.useCount" class="flex items-center gap-2"><span class="text-indigo-200">จำนวนครั้งที่ใช้งานได้: <strong class="text-gray-100">{{ localItem.useCount }}</strong></span></div>
            </div>
            <div v-if="localItem.disarmTrapItems && localItem.disarmTrapItems.length > 0" class="mt-3">
              <h3 class="text-sm font-semibold text-indigo-200 mb-2">ไอเทมปลดกัปดักที่สามารถทำลายได้</h3>
              <div class="flex flex-wrap gap-2">
                <span v-for="disarmItem in localItem.disarmTrapItems" :key="disarmItem" class="px-2 py-1 text-xs text-indigo-200 bg-gray-600 rounded-md">{{ disarmItem }}</span>
              </div>
            </div>
          </section>

          <!-- Bomb -->
          <section v-if="localItem.type === 'ระเบิด'" class="mt-4 bg-gray-700 p-4 rounded-md">
            <h2 class="text-sm font-semibold text-indigo-200 mb-2">ข้อมูลระเบิด</h2>
            <div class="grid grid-cols-2 gap-3 text-xs sm:text-sm">
              <div v-if="localItem.damage" class="flex items-center gap-2"><span class="text-indigo-200">ความเสียหายที่จะได้รับ: <strong class="text-gray-100">{{ localItem.damage }}</strong></span></div>
            </div>
          </section>

          <!-- Vehicle -->
          <section v-if="localItem.type === 'พาหนะ'" class="mt-4 bg-gray-700 p-4 rounded-md">
            <h2 class="text-sm font-semibold text-indigo-200 mb-2">ข้อมูลพาหนะ</h2>
            <div class="grid grid-cols-2 gap-3 text-xs sm:text-sm">
              <div v-if="localItem.fuelLevel" class="flex items-center gap-2"><span class="text-indigo-200">ระดับน้ำมัน: <strong class="text-gray-100">{{ localItem.fuelLevel }}</strong></span></div>
            </div>
          </section>

          <!-- Disarm Trap -->
          <section v-if="localItem.type === 'ปลดกัปดัก'" class="mt-4 bg-gray-700 p-4 rounded-md">
            <h2 class="text-sm font-semibold text-indigo-200 mb-2">ข้อมูลปลดกัปดัก</h2>
            <div class="grid grid-cols-2 gap-3 text-xs sm:text-sm">
              <div v-if="localItem.disarmRoomCount" class="flex items-center gap-2"><span class="text-indigo-200">จำนวนห้องที่สามารถปลดกัปดักได้: <strong class="text-gray-100">{{ localItem.disarmRoomCount }}</strong></span></div>
              <div class="flex items-center gap-2"><span class="text-indigo-200">แจ้งเตือนเมื่อปลดกัปดัก: <strong class="text-gray-100">{{ localItem.notifyOnDisarm ? 'ใช่' : 'ไม่' }}</strong></span></div>
            </div>
            <div v-if="localItem.nonDestroyableTraps && localItem.nonDestroyableTraps.length > 0" class="mt-3">
              <h3 class="text-sm font-semibold text-indigo-200 mb-2">กัปดักที่ไม่สามารถทำลายได้</h3>
              <div class="flex flex-wrap gap-2">
                <span v-for="trap in localItem.nonDestroyableTraps" :key="trap" class="px-2 py-1 text-xs text-indigo-200 bg-gray-600 rounded-md">{{ trap }}</span>
              </div>
            </div>
          </section>

          <!-- Loot -->
          <section v-if="localItem.type === 'ปล้น'" class="mt-4 bg-gray-700 p-4 rounded-md">
            <h2 class="text-sm font-semibold text-indigo-200 mb-2">ข้อมูลการปล้น</h2>
            <div class="grid grid-cols-2 gap-3 text-xs sm:text-sm">
              <div class="flex items-center gap-2"><span class="text-indigo-200">ระยะเวลาที่ต้องรอ: <strong class="text-gray-100">{{ localItem.waitTimeMinutes || 0 }}</strong> นาที</span></div>
              <div class="flex items-center gap-2"><span class="text-indigo-200">จำนวนปล้นไอเทมในกระเป๋าขั้นต่ำ: <strong class="text-gray-100">{{ localItem.minLootItems || 0 }}</strong></span></div>
              <div class="flex items-center gap-2"><span class="text-indigo-200">จำนวนปล้นไอเทมในกระเป๋าขั้นสูงสุด: <strong class="text-gray-100">{{ localItem.maxLootItems || 0 }}</strong></span></div>
              <div class="flex items-center gap-2"><span class="text-indigo-200">จำนวนเหรียญที่ได้รับ: <strong class="text-gray-100">{{ localItem.silverReward || 0 }}</strong></span></div>
              <div class="flex items-center gap-2"><span class="text-indigo-200">จำนวนทองที่ได้รับ: <strong class="text-gray-100">{{ localItem.goldReward || 0 }}</strong></span></div>
              <div class="flex items-center gap-2"><span class="text-indigo-200">จำนวนเพชรที่ได้รับ: <strong class="text-gray-100">{{ localItem.diamondReward || 0 }}</strong></span></div>
            </div>
          </section>

          <!-- Killer Robot -->
          <section v-if="localItem.type === 'หุ่นยนต์สังหาร'" class="mt-4 bg-gray-700 p-4 rounded-md">
            <h2 class="text-sm font-semibold text-indigo-200 mb-2">ข้อมูลหุ่นยนต์สังหาร</h2>
            <div class="grid grid-cols-2 gap-3 text-xs sm:text-sm">
              <div class="flex items-center gap-2"><span class="text-indigo-200">จำนวนครั้งที่อัปเกรดได้: <strong class="text-gray-100">{{ localItem.maxUpgradeCount || 0 }}</strong></span></div>
              <div class="flex items-center gap-2"><span class="text-indigo-200">น้ำหนักที่รับได้: <strong class="text-gray-100">{{ localItem.maxLoad || 0 }}</strong> กก.</span></div>
              <div v-if="localItem.explorableMaps && localItem.explorableMaps.length > 0" class="col-span-2">
                <h3 class="text-sm font-semibold text-indigo-200 mb-2">สถานที่ที่สามารถสำรวจได้</h3>
                <div class="flex flex-wrap gap-2">
                  <span v-for="(map, index) in localItem.explorableMaps" :key="index" class="px-2 py-1 text-xs text-indigo-200 bg-gray-600 rounded-md">{{ map }}</span>
                </div>
              </div>
            </div>
            <div v-if="localItem.upgradeMaterials && localItem.upgradeMaterials.length > 0" class="mt-3">
              <h3 class="text-sm font-semibold text-indigo-200 mb-2">ไอเทมที่ใช้ในการอัปเกรด</h3>
              <div class="space-y-2">
                <div v-for="(material, mIndex) in localItem.upgradeMaterials" :key="mIndex" class="flex items-center gap-2 text-indigo-200">
                  <span>{{ material.itemName }}: <strong class="text-gray-100">{{ material.quantity }}</strong></span>
                </div>
              </div>
            </div>
          </section>

          <!-- Cybernetic Organ -->
          <section v-if="localItem.type === 'อวัยวะจักรกล'" class="mt-4 bg-gray-700 p-4 rounded-md">
            <h2 class="text-sm font-semibold text-indigo-200 mb-2">ข้อมูลอวัยวะจักรกล</h2>
            <div class="grid grid-cols-2 gap-3 text-xs sm:text-sm">
              <div v-if="localItem.upgradeableStats && localItem.upgradeableStats.length > 0" class="col-span-2">
                <h3 class="text-sm font-semibold text-indigo-200 mb-2">ค่าสถานะที่สามารถอัปเกรดได้</h3>
                <div class="flex flex-wrap gap-2">
                  <span v-for="stat in localItem.upgradeableStats" :key="stat" class="px-2 py-1 text-xs text-indigo-200 bg-gray-600 rounded-md">{{ stat.charAt(0).toUpperCase() + stat.slice(1) }}</span>
                </div>
              </div>
            </div>
          </section>

          <!-- Card type -->
          <section v-if="localItem.type === 'การ์ด'" class="mt-4 bg-gray-700 p-4 rounded-md">
            <h2 class="text-sm font-semibold text-indigo-200 mb-2">ข้อมูลการ์ด</h2>
            <div class="grid grid-cols-2 gap-3 text-xs sm:text-sm">
              <div class="flex items-center gap-2"><span class="text-indigo-200">อุปกรณ์ที่สามารถติดตั้งการ์ดนี้ได้: <strong class="text-gray-100">{{ localItem.compatibleEquipment || 'ไม่ระบุ' }}</strong></span></div>
            </div>
          </section>

          <!-- Spirit -->
          <section v-if="localItem.type === 'วิญญาณ'" class="mt-4 bg-gray-700 p-4 rounded-md">
            <h2 class="text-sm font-semibold text-indigo-200 mb-2">ข้อมูลวิญญาณ</h2>
            <div class="grid grid-cols-2 gap-3 text-xs sm:text-sm">
              <div v-if="localItem.subtype === 'วิญญาณเฝ้าห้อง' && localItem.guardDurationHours" class="flex items-center gap-2"><span class="text-indigo-200">ระยะเวลาที่เฝ้า: <strong class="text-gray-100">{{ localItem.guardDurationHours }}</strong> ชั่วโมง</span></div>
              <div v-if="localItem.subtype === 'วิญญาณเฝ้าห้อง' && localItem.fromMap" class="flex items-center gap-2"><span class="text-indigo-200">วิญญาณจากแผนที่: <strong class="text-gray-100">{{ localItem.fromMap }}</strong></span></div>
              <div v-if="localItem.subtype === 'วิญญาณเฝ้าห้อง' && localItem.minSpirits" class="flex items-center gap-2"><span class="text-indigo-200">จำนวนวิญญาณขั้นต่ำ: <strong class="text-gray-100">{{ localItem.minSpirits }}</strong></span></div>
              <div v-if="localItem.subtype === 'วิญญาณเฝ้าห้อง' && localItem.maxSpirits" class="flex items-center gap-2"><span class="text-indigo-200">จำนวนวิญญาณสูงสุด: <strong class="text-gray-100">{{ localItem.maxSpirits }}</strong></span></div>
              <div v-if="['วิญญาณเฝ้าห้อง', 'วิญญาณผู้พิทักษ์', 'วิญญาณดูดทรัพย์'].includes(localItem.subtype) && localItem.cooldownHours" class="flex items-center gap-2"><span class="text-indigo-200">ระยะเวลาคูดาวน์การใช้ครั้งถัดไป: <strong class="text-gray-100">{{ localItem.cooldownHours }}</strong> ชั่วโมง</span></div>
              <div v-if="localItem.subtype === 'วิญญาณค้นหาผู้เล่น' && localItem.eliminatedRooms" class="flex items-center gap-2"><span class="text-indigo-200">จำนวนห้องที่ถูกคัดออก: <strong class="text-gray-100">{{ localItem.eliminatedRooms }}</strong></span></div>
              <div v-if="localItem.subtype === 'วิญญาณผู้พิทักษ์' && localItem.protectCount" class="flex items-center gap-2"><span class="text-indigo-200">จำนวนเกราะที่สามารถป้องกันได้: <strong class="text-gray-100">{{ localItem.protectCount }}</strong></span></div>
              <div v-if="localItem.subtype === 'วิญญาณผู้พิทักษ์' && localItem.protectDurationHours" class="flex items-center gap-2"><span class="text-indigo-200">ระยะเวลาคุ้มกัน: <strong class="text-gray-100">{{ localItem.protectDurationHours }}</strong> ชั่วโมง</span></div>
              <div v-if="localItem.subtype === 'วิญญาณดูดทรัพย์' && localItem.minDiamondLoot" class="flex items-center gap-2"><span class="text-indigo-200">จำนวนปล้นเพชรขั้นต่ำ: <strong class="text-gray-100">{{ localItem.minDiamondLoot }}</strong></span></div>
              <div v-if="localItem.subtype === 'วิญญาณดูดทรัพย์' && localItem.maxDiamondLoot" class="flex items-center gap-2"><span class="text-indigo-200">จำนวนปล้นเพชรขั้นสูงสุด: <strong class="text-gray-100">{{ localItem.maxDiamondLoot }}</strong></span></div>
            </div>
            <div v-if="localItem.requiredPreviousSpirits && localItem.requiredPreviousSpirits.length > 0" class="mt-3">
              <h3 class="text-sm font-semibold text-indigo-200 mb-2">วิญญาณที่ต้องเคยอัญเชิญมาก่อน</h3>
              <div class="flex flex-wrap gap-2">
                <span v-for="spirit in localItem.requiredPreviousSpirits" :key="spirit" class="px-2 py-1 text-xs text-indigo-200 bg-gray-600 rounded-md">{{ spirit }}</span>
              </div>
            </div>
          </section>

        </div>

        <!-- Tab: Drop Locations -->
        <div v-if="activeTab === 'dropLocations'">
          <section>
            <div v-if="dropLoading" class="flex items-center justify-center py-4">
              <div class="animate-spin rounded-full h-6 w-6 border-t-2 border-indigo-600"></div>
            </div>
            <div v-else-if="dropLocations.length === 0" class="text-indigo-200 text-xs sm:text-sm text-center">ไม่มีข้อมูลสถานที่ดรอปสำหรับไอเทมนี้</div>
            <div v-else class="space-y-2 overflow-y-auto max-h-80" id="style-1">
              <div v-for="map in dropLocations" :key="map.id" class="flex items-center gap-3 p-3 bg-gray-700 rounded-md">
                <img :src="map.imageUrl || mapPlaceholder" alt="Map Image" class="w-10 h-10 object-cover rounded-md border border-gray-600" @error="handleImageError" />
                <div>
                  <h3 class="text-xs sm:text-sm font-semibold text-indigo-200">{{ map.name }}</h3>
                  <p class="text-indigo-200 text-xs sm:text-sm">โอกาสดรอป: <strong class="text-gray-100">{{ (map.dropChance / 100).toFixed(2) }}%</strong></p>
                  <p class="text-indigo-200 text-xs sm:text-sm">เลเวลที่ต้องการ: <strong class="text-gray-100">{{ map.requiredLevel }}</strong></p>
                </div>
              </div>
            </div>
          </section>
        </div>

        <!-- Tab: Decompose -->
        <div v-if="activeTab === 'decompose'">
          <section>
            <div v-if="decomposeLoading" class="flex items-center justify-center py-4">
              <div class="animate-spin rounded-full h-6 w-6 border-t-2 border-indigo-600"></div>
            </div>
            <div v-else-if="decomposeItems.length === 0 && localItem.decomposable" class="text-yellow-300 text-xs sm:text-sm text-center">ไม่พบข้อมูลการแยกชิ้นส่วน อาจมีปัญหากับ decomposeList</div>
            <div v-else-if="!localItem.decomposable" class="text-indigo-200 text-xs sm:text-sm text-center">ไอเทมนี้ไม่สามารถแยกชิ้นส่วนได้</div>
            <div v-else class="space-y-2 overflow-y-auto max-h-80" id="style-1">
              <div v-for="decomposeItem in decomposeItems" :key="decomposeItem.itemName" class="flex items-center gap-3 p-3 bg-gray-700 rounded-md">
                <img :src="decomposeItem.img || itemPlaceholder" alt="Decompose Item Image" class="w-10 h-10 object-cover rounded-md border-2" :class="decomposeItem.rarityColors?.borderColor || 'border-gray-600'" @error="handleImageError" />
                <div>
                  <h3 class="text-xs sm:text-sm font-semibold text-indigo-200">{{ decomposeItem.name }}</h3>
                  <p class="text-indigo-200 text-xs sm:text-sm">โอกาส: <strong class="text-gray-100">{{ decomposeItem.chance.toFixed(2) }}%</strong></p>
                </div>
              </div>
            </div>
          </section>
        </div>

        <!-- Tab: Upgrade Preview -->
        <div v-if="activeTab === 'upgrade'">
          <section>
            <h2 class="text-sm font-semibold text-indigo-200 mb-3">ตัวอย่างการอัพเกรด</h2>
            <div v-if="localItem.upgradable && localItem.upgradeTo" class="space-y-2 overflow-y-auto max-h-80" id="style-1">
              <div class="p-3 bg-gray-700 rounded-md">
                <h3 class="text-xs sm:text-sm font-semibold text-indigo-200">อัพเกรดไปยัง: {{ localItem.upgradeTo }}</h3>
                <p class="text-indigo-200 text-xs sm:text-sm">โอกาสสำเร็จ: <strong class="text-gray-100">{{ localItem.upgradeChance.toFixed(2) }}%</strong></p>
              </div>
            </div>
            <div v-else-if="localItem.upgradeList?.length > 0" class="space-y-2 overflow-y-auto max-h-80" id="style-1">
              <div v-for="upgrade in localItem.upgradeList" :key="upgrade.lv" class="p-3 bg-gray-700 rounded-md">
                <div class="mb-2"><h3 class="text-xs sm:text-sm font-semibold text-indigo-200">เลเวล {{ upgrade.lv }}</h3></div>
                <div class="grid grid-cols-2 gap-2 text-xs sm:text-sm text-indigo-200 mb-2">
                  <p>โอกาสสำเร็จ: <strong class="text-gray-100">{{ upgrade.chance.toFixed(2) }}%</strong></p>
                  <p>ราคาอัพเกรด: <strong class="text-gray-100">{{ upgrade.upgradePrice.toLocaleString() }}</strong> เหรียญ</p>
                </div>
                <div class="grid grid-cols-2 gap-2 text-xs sm:text-sm text-indigo-200">
                  <div v-if="upgrade.stats?.stamina > 0">Stamina: <strong class="text-gray-100">{{ upgrade.stats.stamina }}</strong></div>
                  <div v-if="upgrade.stats?.str > 0">Str: <strong class="text-gray-100">{{ upgrade.stats.str }}</strong></div>
                  <div v-if="upgrade.stats?.agi > 0">Agi: <strong class="text-gray-100">{{ upgrade.stats.agi }}</strong></div>
                  <div v-if="upgrade.stats?.int > 0">Int: <strong class="text-gray-100">{{ upgrade.stats.int }}</strong></div>
                  <div v-if="upgrade.stats?.luck > 0">Luck: <strong class="text-gray-100">{{ upgrade.stats.luck }}</strong></div>
                  <div v-if="upgrade.stats?.maxHp > 0">Max HP: <strong class="text-gray-100">{{ upgrade.stats.maxHp }}</strong></div>
                  <div v-if="upgrade.stats?.atk > 0">Atk: <strong class="text-gray-100">{{ upgrade.stats.atk }}</strong></div>
                  <div v-if="upgrade.stats?.evasion > 0">Evasion: <strong class="text-gray-100">{{ upgrade.stats.evasion }}</strong></div>
                  <div v-if="upgrade.stats?.critical > 0">Critical: <strong class="text-gray-100">{{ upgrade.stats.critical }}</strong></div>
                  <div v-if="upgrade.stats?.defense > 0">Defense: <strong class="text-gray-100">{{ upgrade.stats.defense }}</strong></div>
                </div>
              </div>
            </div>
            <div v-else class="text-indigo-200 text-xs sm:text-sm text-center">ไม่มีข้อมูลการอัพเกรดสำหรับไอเทมนี้</div>
          </section>
        </div>
      </div>

      <!-- Footer -->
      <div class="p-4 border-t border-gray-700 flex justify-end">
        <button
          @click="closeModal"
          class="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-md text-xs sm:text-sm font-medium transition-colors duration-200 border border-gray-600"
          aria-label="ปิด"
        >ปิด</button>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted, onUnmounted, watch } from "vue";
import { projectFirestore, projectFunctions } from "@/firebase/config";
import getUser from "@/composables/getUser";
import gameItems from "@/data/gameItems.json";

export default {
  name: "ItemModal",
  props: {
    item: { type: Object, required: true },
    itemId: { type: String, required: true },
  },
  emits: ["close"],
  setup(props, { emit }) {
    const { user } = getUser();
    const activeTab = ref("info");
    const localItem = ref({});
    const cardData = ref([]);
    const availableCards = ref([]);
    const availableCardsLoading = ref(false);
    const dropLocations = ref([]);
    const dropLoading = ref(false);
    const decomposeItems = ref([]);
    const decomposeLoading = ref(false);
    const itemLoading = ref(true);
    const error = ref('');

    // ตัวแปรป้องกันการดึงข้อมูลซ้ำ
    let isFetchingCards = false;
    let lastFetchedEquipmentName = '';
    let lastFetchedCardNames = '';

    // stat preview toggle
    const previewCardId = ref(null);
    const statPreviewKeys = ['stamina', 'str', 'int', 'agi', 'luck', 'maxHp', 'atk', 'evasion', 'critical', 'defense'];

    const togglePreview = (cardId) => {
      previewCardId.value = previewCardId.value === cardId ? null : cardId;
    };

    // คำนวณ stat ปัจจุบันของอวัยวะ (base + upgrade + card ที่ติดตั้งแล้ว)
    const getCurrentStat = (statKey) => {
      const b = localItem.value.stats || {};
      const u = localItem.value.upgradeStats || {};
      const c = localItem.value.cardStats || {};
      return (b[statKey] || 0) + (u[statKey] || 0) + (c[statKey] || 0);
    };

    const itemPlaceholder = 'data:image/svg+xml;base64,Jmx0O3N2ZyB4bWxucz0mcXVvdDtodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyZxdW90OyB3aWR0aD0mcXVvdDsxMDAmcXVvdDsgaGVpZ2h0PSZxdW90OzEwMCZxdW90OyZndDsmbHQ7cmVjdCB3aWR0aD0mcXVvdDsxMDAmcXVvdDsgaGVpZ2h0PSZxdW90OzEwMCZxdW90OyBmaWxsPSZxdW90OyNjY2MmcXVvdDsvJmd0OyZsdDt0ZXh0IHg9JnF1b3Q7NTAmcXVvdDsgeT0mcXVvdDs1NSZxdW90OyBmb250LXNpemU9JnF1b3Q7MTUmcXVvdDsgdGV4dC1hbmNob3I9JnF1b3Q7bWlkZGxlJnF1b3Q7IGZpbGw9JnF1b3Q7IzAwMCZxdW90OyZndDtJdGVtJmx0Oy90ZXh0Jmd0OyZsdDsvc3ZnJmd0Ow==';
    const mapPlaceholder = 'data:image/svg+xml;base64,Jmx0O3N2ZyB4bWxucz0mcXVvdDtodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyZxdW90OyB3aWR0aD0mcXVvdDsxMDAmcXVvdDsgaGVpZ2h0PSZxdW90OzEwMCZxdW90OyZndDsmbHQ7cmVjdCB3aWR0aD0mcXVvdDsxMDAmcXVvdDsgaGVpZ2h0PSZxdW90OzEwMCZxdW90OyBmaWxsPSZxdW90OyNjY2MmcXVvdDsvJmd0OyZsdDt0ZXh0IHg9JnF1b3Q7NTAmcXVvdDsgeT0mcXVvdDs1NSZxdW90OyBmb250LXNpemU9JnF1b3Q7MTUmcXVvdDsgdGV4dC1hbmNob3I9JnF1b3Q7bWlkZGxlJnF1b3Q7IGZpbGw9JnF1b3Q7IzAwMCZxdW90OyZndDtNYXAmbHQ7L3RleHQmZ3Q7Jmx0Oy9zdmcmZ3Q7';

    const statIcons = {
      stamina: 'fas fa-heart', str: 'fas fa-dumbbell', int: 'fas fa-brain',
      agi: 'fas fa-running', luck: 'fas fa-dice', maxHp: 'fas fa-heartbeat',
      atk: 'fas fa-fist-raised', evasion: 'fas fa-running', critical: 'fas fa-bolt', defense: 'fas fa-shield-alt',
    };

    const upgradableTypes = ['หมวก', 'แว่นตา', 'เสื้อ', 'เสื้อคลุม', 'เกราะ', 'ปลอกแขน', 'ถุงมือ', 'กางเกง', 'สนับแข้ง', 'รองเท้า', 'เข็มขัด', 'สร้อยคอ', 'แหวน', 'อาวุธ', 'อาวุธสำรอง', 'ตะกรุด', 'ผ้ายันต์', 'สายสิญจน์'];

    const isUpgradable = computed(() => upgradableTypes.includes(localItem.value.type) || localItem.value.type === 'อวัยวะจักรกล');
    const isOwner = computed(() => !localItem.value.ownerUid || localItem.value.ownerUid === user.value?.uid);
    const currentLevel = computed(() => localItem.value.type === 'อวัยวะจักรกล' ? (localItem.value.upgradeCount || 0) : (localItem.value.level || 0));
    const maxUpgradeCount = computed(() => localItem.value.type === 'อวัยวะจักรกล' ? 0 : (localItem.value.maxUpgradeCount || 3));
    const remainingUpgrades = computed(() => maxUpgradeCount.value - currentLevel.value);
    const isStatUpgradeable = (stat) => localItem.value.type === 'อวัยวะจักรกล' && localItem.value.upgradeableStats?.includes(stat);

    const calculatedStats = computed(() => {
      const b = localItem.value.stats || {}, u = localItem.value.upgradeStats || {}, c = localItem.value.cardStats || {};
      return {
        stamina: (b.stamina || 0) + (u.stamina || 0) + (c.stamina || 0),
        str: (b.str || 0) + (u.str || 0) + (c.str || 0),
        int: (b.int || 0) + (u.int || 0) + (c.int || 0),
        agi: (b.agi || 0) + (u.agi || 0) + (c.agi || 0),
        luck: (b.luck || 0) + (u.luck || 0) + (c.luck || 0),
      };
    });

    const calculatedSubStats = computed(() => {
      const b = localItem.value.stats || {}, u = localItem.value.upgradeStats || {}, c = localItem.value.cardStats || {};
      return {
        maxHp: (b.maxHp || 0) + (u.maxHp || 0) + (c.maxHp || 0),
        atk: (b.atk || 0) + (u.atk || 0) + (c.atk || 0),
        evasion: (b.evasion || 0) + (u.evasion || 0) + (c.evasion || 0),
        critical: (b.critical || 0) + (u.critical || 0) + (c.critical || 0),
        defense: (b.defense || 0) + (u.defense || 0) + (c.defense || 0),
      };
    });

    const hasStats = computed(() => {
      if (localItem.value.type === 'ปลดกัปดัก' || localItem.value.type === 'ปล้น') return false;
      return Object.values(calculatedStats.value).some(v => v > 0) ||
        Object.values(calculatedSubStats.value).some(v => v > 0) ||
        (localItem.value.type === 'อวัยวะจักรกล' && Array.isArray(localItem.value.upgradeableStats) && localItem.value.upgradeableStats.length > 0);
    });

    const hasMainStats = computed(() => Object.values(calculatedStats.value).some(v => v > 0) || (localItem.value.type === 'อวัยวะจักรกล' && localItem.value.upgradeableStats?.length > 0));
    const hasSubStats = computed(() => Object.values(calculatedSubStats.value).some(v => v > 0));
    const showStamina = computed(() => calculatedStats.value.stamina > 0 || (localItem.value.type === 'อวัยวะจักรกล' && localItem.value.upgradeableStats?.includes('stamina')));
    const showStr = computed(() => calculatedStats.value.str > 0 || (localItem.value.type === 'อวัยวะจักรกล' && localItem.value.upgradeableStats?.includes('str')));
    const showInt = computed(() => calculatedStats.value.int > 0 || (localItem.value.type === 'อวัยวะจักรกล' && localItem.value.upgradeableStats?.includes('int')));
    const showAgi = computed(() => calculatedStats.value.agi > 0 || (localItem.value.type === 'อวัยวะจักรกล' && localItem.value.upgradeableStats?.includes('agi')));
    const showLuck = computed(() => calculatedStats.value.luck > 0 || (localItem.value.type === 'อวัยวะจักรกล' && localItem.value.upgradeableStats?.includes('luck')));

    // Fetch item data from props (new object-based system)
    const fetchItemData = async () => {
      itemLoading.value = true;
      error.value = '';
      try {
        const item = props.item || {};
        // Try templateId first, then fallback to name for gameItems lookup
        let tpl = gameItems[item.templateId] || {};
        if (!tpl.name && item.name) {
          tpl = gameItems[item.name] || {};
        }
        // Fallback: ใช้ itemId ค้นหาใน gameItems ถ้าไม่พบจากข้างต้น
        if (!tpl.name && props.itemId) {
          tpl = gameItems[props.itemId] || {};
        }
        localItem.value = {
          ...tpl,
          ...item,
          stats: tpl.stats || {},
          upgradeStats: item.states?.upgradeStats || item.upgradeStats || {},
          cardStats: item.states?.cardStats || item.cardStats || {},
          cardIds: (item.states?.cardSlots || item.cardSlots || []).map(s => s.cardTemplateId || s).filter(Boolean),
          cardNames: [],
          upgradeableStats: tpl.upgradeableStats || [],
          decomposeList: tpl.decomposeList || [],
          ownerUid: item.ownerUid || null,
          level: item.states?.level ?? item.level ?? null,
          upgradeCount: item.states?.upgradeCount ?? item.upgradeCount ?? 0,
          rarity: item.states?.rarity ?? item.rarity ?? tpl.rarity ?? 1,
          rarityColors: item.states?.rarityColors ?? item.rarityColors ?? tpl.rarityColors ?? null,
          locked: item.states?.locked ?? item.locked ?? false,
          protected: item.states?.protected ?? item.protected ?? false,
          statPoints: item.states?.statPoints ?? item.statPoints ?? 0,
        };

        // ดึง card names จาก cardSlots
        if (item.states?.cardSlots?.length) {
          for (const slot of item.states.cardSlots) {
            if (slot.cardTemplateId && gameItems[slot.cardTemplateId]) {
              localItem.value.cardNames.push(gameItems[slot.cardTemplateId].name);
            }
          }
        }

        itemLoading.value = false;

        if (localItem.value.name) {
          fetchDropLocations();
          const cardIds = localItem.value.cardIds || [];
          await fetchCardsData(cardIds);
          if (localItem.value.type === 'อวัยวะจักรกล') {
            await fetchAvailableCards();
          }
          if (localItem.value.decomposable) {
            fetchDecomposeItems();
          }
        }
      } catch (err) {
        console.error("Error fetching item data:", err);
        error.value = err.message || 'เกิดข้อผิดพลาดในการโหลดข้อมูลไอเทม';
        itemLoading.value = false;
      }
    };

    // Fetch installed cards from gameItems.json (new object-based system)
    const fetchCardsData = async (cardIds) => {
      if (!cardIds || cardIds.length === 0) { cardData.value = []; return; }
      try {
        const cards = cardIds.map((templateId) => {
          const card = gameItems[templateId];
          if (card) {
            return {
              id: templateId,
              ...card,
              stats: card.stats || {},
              rarityColors: card.rarityColors || { borderColor: 'border-gray-600' }
            };
          }
          return null;
        });
        cardData.value = cards
          .filter(c => c !== null)
          .sort((a, b) => (a.rarity || 0) - (b.rarity || 0));
      } catch (err) {
        console.error("Error fetching cards data:", err);
        cardData.value = [];
      }
    };

    // Fetch available (not installed) cards
    // ป้องกันซ้ำด้วย isFetchingCards และเปรียบเทียบ equipmentName + cardNames
    const fetchAvailableCards = async () => {
      if (localItem.value.type !== 'อวัยวะจักรกล' || !localItem.value.name) {
        availableCards.value = [];
        return;
      }

      const currentCardNames = JSON.stringify(localItem.value.cardNames || []);

      // ข้ามถ้ากำลังดึงอยู่ หรือทั้ง equipmentName และ cardNames ไม่เปลี่ยน
      if (isFetchingCards) return;
      if (lastFetchedEquipmentName === localItem.value.name && lastFetchedCardNames === currentCardNames) return;

      isFetchingCards = true;
      lastFetchedEquipmentName = localItem.value.name;
      lastFetchedCardNames = currentCardNames;
      availableCardsLoading.value = true;

      try {
        const installedCardNames = localItem.value.cardNames || [];
        const seenNames = new Set(); // deduplicate โดยชื่อการ์ด
        const cards = [];

        Object.entries(gameItems).forEach(([id, data]) => {
          if (data.type === 'การ์ด' && data.compatibleEquipment === localItem.value.name) {
            if (!installedCardNames.includes(data.name) && !seenNames.has(data.name)) {
              seenNames.add(data.name);
              cards.push({
                id,
                ...data,
                stats: data.stats || {},
                rarityColors: data.rarityColors || { borderColor: 'border-gray-600' },
              });
            }
          }
        });

        availableCards.value = cards.sort((a, b) => (a.rarity || 0) - (b.rarity || 0));
      } catch (err) {
        console.error('Error fetching available cards:', err);
        availableCards.value = [];
      } finally {
        availableCardsLoading.value = false;
        isFetchingCards = false;
      }
    };

    const handleImageError = (event) => {
      event.target.src = event.target.alt.includes("Map") ? mapPlaceholder : itemPlaceholder;
    };

    const fetchDropLocations = async () => {
      if (!localItem.value.name) { dropLocations.value = []; return; }
      dropLoading.value = true;
      try {
        const snapshot = await projectFirestore.collection("maps").where("rewardItemNames", "array-contains", localItem.value.name).get();
        const locations = [];
        snapshot.forEach(doc => {
          const mapData = doc.data();
          const reward = mapData.rewards.find(r => r.itemName === localItem.value.name);
          if (reward) locations.push({ id: doc.id, name: mapData.name, imageUrl: mapData.imageUrl || mapPlaceholder, dropChance: reward.dropChance || 0, requiredLevel: mapData.requiredLevel || 1 });
        });
        dropLocations.value = locations.sort((a, b) => b.dropChance - a.dropChance);
      } catch (err) {
        console.error("Error fetching drop locations:", err);
        dropLocations.value = [];
      } finally {
        dropLoading.value = false;
      }
    };

    const fetchDecomposeItems = async () => {
      if (!localItem.value.decomposable || !localItem.value.decomposeList?.length) {
        decomposeItems.value = [];
        decomposeLoading.value = false;
        return;
      }
      decomposeLoading.value = true;
      try {
        const results = localItem.value.decomposeList.map((decompose) => {
          if (!decompose.itemName) return null;
          const data = gameItems[decompose.itemName];
          return {
            itemName: decompose.itemName,
            name: data?.name || decompose.itemName,
            img: data?.img || itemPlaceholder,
            chance: decompose.chance || 0,
            rarityColors: data?.rarityColors || { borderColor: "border-indigo-400" }
          };
        }).filter(i => i !== null);
        decomposeItems.value = results.sort((a, b) => b.chance - a.chance);
      } catch (err) {
        console.error("Error fetching decompose items:", err);
        decomposeItems.value = [];
      } finally {
        decomposeLoading.value = false;
      }
    };

    const closeModal = () => {
      emit("close");
    };

    onMounted(async () => {
      await fetchItemData();
    });

    // Watch เฉพาะ field ที่สำคัญ เพื่อลด trigger ซ้ำจาก onSnapshot
    watch(
      () => [
        localItem.value.name,
        localItem.value.type,
        JSON.stringify(localItem.value.cardIds),
        JSON.stringify(localItem.value.cardNames),
        localItem.value.decomposable,
      ],
      async ([name, type, cardIdsStr, , decomposable], [oldName]) => {
        if (!name) return;

        if (name !== oldName) {
          fetchDropLocations();
        }

        const cardIds = JSON.parse(cardIdsStr || '[]');
        await fetchCardsData(cardIds);

        if (type === 'อวัยวะจักรกล') {
          await fetchAvailableCards();
        }

        if (decomposable) {
          fetchDecomposeItems();
        }
      }
    );

    onUnmounted(() => {
      // No Firestore subscription to clean up in new system
    });

    return {
      activeTab, localItem, cardData, availableCards, availableCardsLoading,
      dropLocations, dropLoading, decomposeItems, decomposeLoading,
      itemLoading, error, hasStats, hasMainStats, hasSubStats,
      showStamina, showStr, showInt, showAgi, showLuck,
      isUpgradable, isStatUpgradeable, isOwner,
      calculatedStats, calculatedSubStats, statIcons,
      currentLevel, remainingUpgrades, closeModal, handleImageError,
      itemPlaceholder, mapPlaceholder,
      previewCardId, statPreviewKeys, togglePreview, getCurrentStat,
    };
  },
};
</script>

<style scoped>
::-webkit-scrollbar { width: 5px; }
::-webkit-scrollbar-track { background: #374151; border-radius: 10px; }
::-webkit-scrollbar-thumb { background: #4b5563; border-radius: 10px; }
::-webkit-scrollbar-thumb:hover { background: #6b7280; }
.animate-spin { animation: spin 1s linear infinite; }
@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
button:not(:disabled):active { transform: scale(0.98); }
</style>